import { DocketsRecord, DocketsSourceOptions, pb } from "@/utils/PocketBaseAdapter";
import { markEventTicketsPaidForDocket } from "@/utils/markEventTicketsPaid";
import { useUserStore } from "@/utils/UserStore";
import { moment } from "@/utils/useTimeZone";
import { defineStore } from "pinia";
import { toast } from "vue3-toastify";
import { useCheckoutStore } from "./Docket";
import { useDocket } from "./useDocket";

export const useDocketStore = defineStore("DocketStore", () => {
  // Interface
  interface Discount {
    productDiscount?: number;
    bookingDiscount?: number;
    eventDiscount?: number;
    foodDiscount?: number;
  }

  // Store
  const docketStore = useCheckoutStore();
  const docket = useDocket();
  const userStore = useUserStore();

  const { userId, currentLocation } = storeToRefs(userStore);
  const {
    processingItems,
    payingNow,
    partialPayingAmount,
    balanceToPay,
    splitBy,
    customeSplitValue,
    isFullScreenLoading,
    activePaymentTab,
    remainingBalance,
    intialSplit,
    docketNumber,
    docketDescription,
    posDiscount,
  } = storeToRefs(docketStore);

  // composable
  const { getDocketLinesItems } = docket;

  // Data
  const dockets = ref<DocketsRecord[]>([]);
  const docketDiscountData = ref<Discount>({});
  const terminalList = ref<any[]>([]);
  const docketStatus = ref<string[]>(["Open", "Partial"]);
  const docketSource = ref<DocketsSourceOptions[]>([DocketsSourceOptions.POS, DocketsSourceOptions.Kiosk]);
  const isInitialized = ref<boolean>(false);
  const createdGiftVouchers = ref<any[]>([]);
  const createdEvents = ref<any[]>([]);
  const maxDiscountApplicable = ref<number>(0);
  const showGiftVoucherEventDialog = ref(false);

  // Actions
  // 👉 Fetch all Dockets
  const fetchDockets = (params: any) => {
    let filters = "";

    if (params.location) {
      if (filters) filters += ` && location="${params?.location}"`;
      else filters = `location="${params?.location}"`;
    } else {
      filters = `location="${currentLocation.value}"`;
    }

    if (params.status && params.status?.length) {
      const statusFilter = `(${params.status.map((status: any) => `status="${status}"`).join(" || ")})`;

      if (filters) filters += ` && ${statusFilter}`;
      else filters = `${statusFilter}`;
    }

    if (params.q) {
      if (filters)
        filters += `&& (description ~ "${params.q}" || docketNumber ~ "${params.q}" || total = "${params.q}" || due = "${params.q}") `;
      else
        filters = `description ~ "${params.q}" || docketNumber ~ "${params.q}"`;
    }

    if(params.user){
      filters = `(user="${params.user}" || user="") && status != "Paid" && status != "Closed"`
    }

    let sortParam = "-docketNumber";
    if (params?.sortBy.length) {
      sortParam =
        params?.sortBy[0].order === "desc"
          ? `-${params?.sortBy[0].key}`
          : params?.sortBy[0].key;
    }

    if(params?.source && params?.source.length > 0){
      filters += (filters == '' ? '(' : ' && (') + params?.source.map(p => 'source="' + p + '"').join('||') + ")"
    }

    return pb
      .collection("dockets")
      .getList(params.page, params.itemsPerPage, {
        filter: filters,
        expand: "user,location, docketLines_via_docket,docketPayments_via_docket, kiosks_via_tabDocket",
        sort: sortParam ? sortParam : "",
      });
  };

  // Helper function to get item discount based on discountApplied field
  const getItemDiscount = (item: any) => {
    let discount = 0;
    if (item.discountApplied === "Line") {
      discount = item.lineDiscount || 0;
    } else if (item.discountApplied === "Docket Manual" || item.discountApplied === "Membership" || item.discountApplied === "Code") {
      discount = item.docketDiscount || 0;
    } else if (item.discountApplied === "TOD") {
      discount = item.todAdjustment < 0 ? -item.todAdjustment : 0;
    }
    return discount;
  };

  const totalPayingNow = () => {
    return processingItems.value.reduce(
      (total, item: any) => {
        const discount = getItemDiscount(item);
        const discountMultiplier = 1 - discount / 100;
        const itemTotal =
          (item?.itemPrice || 0) * (item.inProcessing || 0) * discountMultiplier;
        return total + itemTotal;
      },
      0,
    );
  };

  // Update all required field of store after payment tab change
  const paymentModeTabChanging = async (tab: any, docketId: any) => {
    try {
      // split payment tab
      if (tab === 1) {
        // not in partial tab clear the partial amount
        partialPayingAmount.value = 0;

        // set payingNow amount to 0 and splitBy to 1
        payingNow.value = 0;
        splitBy.value = 1;

        // set initial split to 1 as well
        intialSplit.value = 1;
      }

      // items payment tab
      if (tab == 2) {
        // closure function to calculate payingNow Amount

        // calculate and assign the value into payingNow
        if (processingItems.value.length) {
          payingNow.value = totalPayingNow();
        } else {
          // no item in processing
          payingNow.value = 0;
        }

        // not in partial tab clear the partial amount
        partialPayingAmount.value = 0;

        // not in split tab, clear the splitBy
        splitBy.value = 1;
      }

      // partial payment tab
      if (tab == 3) {
        // not in split tab, clear the splitBy
        splitBy.value = 1;
        payingNow.value = 0;
      }

      // remainder payment tab
      if (tab == 4) {
        // not in partial tab clear the partial amount
        partialPayingAmount.value = 0;

        // not in split tab, clear the splitBy
        splitBy.value = 1;

        // update paying now to full remaining
        payingNow.value = balanceToPay.value;
      }

      // not in item tab and there any processing items clear all the item and show in available list
      if (tab != 2) {
        try {
          let filter = `docket="${docketId}"`;
          const docketLineItems = await pb
            .collection("docketLines")
            .getFullList({
              filter: filter,
            });

          if (docketLineItems) {
            const promises: any[] = [];
            docketLineItems.forEach(async (dLine) => {
              // any items goes in processing state only need to update otherwise ignore unnecessary API call
              if (dLine.processing > dLine.processed) {
                // make all the items move back to available
                dLine.processing = dLine.processed;
                dLine.updatedBy = userId.value;
                promises.push(
                  pb.collection("docketLines").update(dLine.id, dLine),
                );
              }
            });

            // after update completely then refetch list
            let record = await Promise.all(promises);
            if (record) {
              // fetch the all list items
              await getDocketLinesItems(docketId);
              processingItems.value = [];
            }
          }
        } catch (e) {}
      }
    } catch (e) {}
  };

  // Payment approved, so update all the processed key
  const paymentApproved = async (docketId: any, payment: any) => {
    try {
      // start loading until fetch the details
      isFullScreenLoading.value = true;

      // split tab
      if (activePaymentTab.value == 1) {
        if (customeSplitValue.value > 1) customeSplitValue.value -= 1;

        if (splitBy.value > 2) {
          splitBy.value -= 1;
          // update the splitBy
          payingNow.value = balanceToPay.value / splitBy.value;
        } else if (splitBy.value == 2) {
          splitBy.value = 1;
          intialSplit.value = 1;

          // update the splitBy
          payingNow.value = balanceToPay.value / 1;
        }
      }

       const docketLineItems = await pb
        .collection("docketLines")
        .getFullList({
          filter: `docket="${docketId}"`,
          expand: 'product, product.category',
          requestKey: null
        });

      // Capture processingItems before they get cleared (for item tab filtering)
      const capturedProcessingItems = [...(processingItems.value || [])];

      // in item tab update the processed key
      if (activePaymentTab.value == 2) {
        try {
          // update processed key as payment success
          let promises: any[] = [];
          docketLineItems.forEach(async (item) => {
            // update processed key as number of quantity of item as its processed payment
            item.processed = item.processing;
            item.updatedBy = userId.value;
            promises.push(
              await pb.collection("docketLines").update(item.id, item),
            );
          });

          // after update completely then refetch list
          const record = await Promise.all(promises);
          if (record) {
            // fetch the all list items
            await getDocketLinesItems(docketId);

            payingNow.value = totalPayingNow();
            processingItems.value = [];
          }
        } catch (e) {}
      }

      // All prepTime options trigger prep on payment (even partial payment)
      try {
        const data = await pb.send(`/bbapi/productprep?did=${docketId}&create=true`, {
          method: 'GET',
        });

        if (!data.success) {
          const errorMessage = data.error || data.message || `Server error: ${data.status}`;
          toast.error("Error creating prep order: " + errorMessage, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
          throw new Error(errorMessage);
        } else {
          if(data.action === 'created') {
            toast.success("Prep Order Sent!", {
              position: toast.POSITION.BOTTOM_RIGHT,
            });
          }
        }
      } catch (error) {
        console.error("Error during prep on payment:", error);
        toast.error("Error creating prep order: " + error, {
          position: toast.POSITION.BOTTOM_RIGHT,
        });
      }

      // Get Docket paid - and take that amount off the booking
      // Derive booking records from freshly-fetched docketLineItems to avoid
      // stale/empty originalDocketItems in the store (race with getDocketLinesItems).
      // Include Product lines — booking extras are products that contribute to booking due.
      const allBookingLines = (docketLineItems as any[]).filter(
        (item: any) => item?.booking
      );
      const seenBookingIds = new Set<string>();
      const bookingRecords = allBookingLines.filter((item: any) => {
        if (seenBookingIds.has(item.booking)) return false;
        seenBookingIds.add(item.booking);
        return true;
      });

      if (bookingRecords.length > 0) {
        let remainingAmount = payment.amount;

        // When in item tab (tab 2), determine which bookings should be paid based on processingItems
        const isItemTab = activePaymentTab.value === 2;

        // Get IDs from captured processing items
        const processingItemIds = capturedProcessingItems.map((item: any) => item?.id) || [];

        // Get booking IDs from processing items that have a booking reference
        const processingBookingRefs = capturedProcessingItems
          .filter((item: any) => item?.booking)
          .map((item: any) => item.booking);

        for (const record of bookingRecords) {
          // Stop if no payment amount left to distribute
          if (remainingAmount <= 0) break;

          // If in item tab, only pay this booking if any of its docket lines are in processingItems
          if (isItemTab) {
            const bookingLineIds = allBookingLines
              .filter((item: any) => item.booking === record.booking)
              .map((item: any) => item.id);
            const hasSelectedLine = bookingLineIds.some((id: string) => processingItemIds.includes(id));
            const hasSelectedBookingRef = processingBookingRefs.includes(record.booking);
            if (!hasSelectedLine && !hasSelectedBookingRef) {
              continue;
            }
          }

          // Get booking data
          const bookingData = await pb.collection("bookings").getOne(record?.booking, {
            fields: "due,paid,id,state,payMethod,owner,users",
          });

          // If booking data exists, update it
          if (bookingData) {
            // Calculate how much to pay for this booking (don't overpay)
            let exsistingDue = bookingData.due
            // BUG fix for terminal payments having a booking.due of 0 sometimes
            if(exsistingDue === 0 && bookingData.paid === 0){
              exsistingDue = docketLineItems.filter((item: any) => item?.booking === bookingData.id).reduce((acc: any, dl: any) => acc + dl.lineTotal, 0)
            }
            const amountToPayThisBooking = Math.min(remainingAmount, exsistingDue);
            // Deduct from remaining amount
            remainingAmount -= amountToPayThisBooking;

            let paymentMethods = Array.from(
              new Set([...(bookingData?.payMethod || []), "POS"]),
            )

            if(paymentMethods.length > 2){
              paymentMethods.splice(2)
            }

            const balanceDue = exsistingDue - amountToPayThisBooking;
            const startingState = bookingData.state

            await pb.collection("bookings").update(bookingData.id, {
              paid: bookingData?.paid + amountToPayThisBooking,
              due: balanceDue,
              state: balanceDue === 0 ? 'Confirmed' : startingState,
              payMethod: paymentMethods,
            });

            let state = bookingData.state
            if(balanceDue === 0 && startingState != 'Confirmed'){
              state = "Confirmed"
            }
          }
        }
      }

      // partial tab
      if (activePaymentTab.value == 3) {
        partialPayingAmount.value = 0;
        payingNow.value = 0;
      }

      if (activePaymentTab.value == 4) {
        payingNow.value = remainingBalance.value;
      }

      const giftVoucherEventDetails = docketLineItems?.filter(
        (el: any) =>
          el?.transactionType === "Gift Voucher" ||
          (el?.transactionType === "Event" && el?.product && !el?.booking) ||
          el?.transactionType === "Coupon"
      )
      .map((el: any) => ({
        itemPrice: el?.itemPrice,
        qty: el?.qty,
        docketLineId: el?.id,
        type: el?.transactionType,
        product: el?.product,
        ...(el?.transactionType === "Coupon" && {
          couponQty: el?.couponQty,
          couponType: el?.couponType,
        }),
      }));

      // Check actual docket due amount to avoid race condition with realtime subscription
      const currentDocket = await pb.collection("dockets").getOne(docketId);
      const docketFullyPaid = currentDocket?.due <= 0;

      // Must run BEFORE the "mark already-existing tickets paid" block below. We only want to show the dialog for newly 
      // created event tickets not "pay in store" ones
      if(docketFullyPaid && giftVoucherEventDetails.length > 0) {
        await handleGenerateGiftVoucherOrEventTickets(giftVoucherEventDetails);
        if(createdGiftVouchers.value.length || createdEvents.value.length){
          showGiftVoucherEventDialog.value = true;
        }
      }

      // Mark any already-existing event tickets on this docket as paid.
      // Must run AFTER handleGenerateGiftVoucherOrEventTickets above — see comment there.
      if (docketFullyPaid) {
        await markEventTicketsPaidForDocket(docketId);
      }

      // stop the loading
      isFullScreenLoading.value = false;
    } catch (e) {
      console.error("Error in paymentApproved:", e);
      // stop the loading
      isFullScreenLoading.value = false;
    }
  };

  // Fetch terminal list
  const getAvailableTerminalList = async () => {
    try {
      const records = await pb.collection("paymentTerminals").getFullList({
        filter: `location="${currentLocation.value}"`,
      });
      if (records) {
        let listItem = [] as any;
        records.forEach((el: any) => {
          if (el.enabled) {
            el.label = `# ${el.terminalNumber} ${el.description}`;
            listItem.push(el);
          }
        });

        terminalList.value = listItem;
      }
    } catch (e) {}
  };

  const handleGenerateGiftVoucherOrEventTickets = async (
    giftVoucherEvents: any[],
  ) => {
    try {
      for (let i = 0; i < giftVoucherEvents.length; i++) {
        const item = giftVoucherEvents[i];

        if (item?.type === "Gift Voucher" && item?.qty > 0) {
          let vouchers: any[] = [];
          for (let attempt = 0; attempt < 5; attempt++) {
            if (attempt > 0) await new Promise(resolve => setTimeout(resolve, 1000));
            vouchers = await pb.collection("giftVouchers").getFullList({
              filter: `docketLine="${item.docketLineId}"`,
            });
            if (vouchers.length > 0) {
              if (attempt > 0) console.log(`POS - gift vouchers found on attempt ${attempt + 1}`);
              break;
            }
          }
          createdGiftVouchers.value.push(...vouchers);
        }

        if (item?.type === "Event" && item?.qty > 0) {
          let eventTickets: any[] = [];
          for (let attempt = 0; attempt < 5; attempt++) {
            if (attempt > 0) await new Promise(resolve => setTimeout(resolve, 1000));
            // paid = true intentionally excludes pre-existing pay-in-store tickets
            eventTickets = await pb.collection("eventTickets").getFullList({
              filter: `docketLine="${item.docketLineId}" && paid = true`,
            });
            console.log('eventTickets', eventTickets)
            if (eventTickets.length > 0) {
              if (attempt > 0) console.log(`POS - event tickets found on attempt ${attempt + 1}`);
              break;
            }
          }
          createdEvents.value.push(...eventTickets);
        }

        if (item?.type === "Coupon" && item?.qty > 0) {
          let couponuser = userId.value
          try{
            let docketLine = await pb.collection("docketLines").getOne(item.docketLineId, {expand:'docket'})
            if(docketLine && docketLine?.expand?.docket?.user){
              couponuser = docketLine.expand.docket.user
            }
          } catch(e){}

          let couponTypeAfterHoursOnly = false;
          let activity;
          if (item?.couponType) {
            try {
              const couponType = await pb.collection("couponTypes").getOne(item.couponType);
              couponTypeAfterHoursOnly = couponType?.afterHoursOnly ?? false;
              activity = couponType.activity;
            } catch(e) {}
          }

          let expiryTime = moment()
            .add(2, "years")
            .format("YYYY-MM-DD 23:59:59");

          const coupon = await pb.collection("coupons").create({
            name: "Coupon Purchase",
            qty: item?.qty * item?.couponQty,
            qtyRemaining: item?.qty * item?.couponQty,
            expiryTime: expiryTime,
            expired: false,
            user: couponuser,
            afterHoursOnly: couponTypeAfterHoursOnly,
            activity: activity,
          });

          // Update the docketLine with the array of event ticket IDs
          await pb.collection("docketLines").update(item.docketLineId, {
            coupon: coupon.id,
          });
        }
      }
    } catch (e) {
      console.error("Error in handleGenerateGiftVoucherOrEventTickets:", e);
    }
  };

  // Initialization logic inside the docketStore
  const init = async () => {
    if (!isInitialized.value) {
      isInitialized.value = true; // Ensure it's initialized only once
    }
  };

  // Automatically initialize when the store is accessed or used
  watchEffect(async () => {
    if (!isInitialized.value) {
      await init();
    }
  });

  return {
    dockets,
    posDiscount,
    docketNumber,
    docketDescription,
    docketDiscountData,
    terminalList,
    docketStatus,
    docketSource,
    createdGiftVouchers,
    createdEvents,
    maxDiscountApplicable,
    showGiftVoucherEventDialog,
    fetchDockets,
    paymentModeTabChanging,
    paymentApproved,
    getAvailableTerminalList,
    handleGenerateGiftVoucherOrEventTickets,
  };
});
